// src/pages/PurchaseRecord/components/exportPurchaseRecordCsv.js
import { formatToCompanyTimezone, numberFormatTax } from "../../../../../lib/functions";

const escape = (val) => {
  const str = String(val ?? "");
  return str.includes(",") || str.includes('"') || str.includes("\n")
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

const money = (v) => Number(v || 0).toFixed(2);
const fmtDate = (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-");
const itemFamily = ["By Item", "By Group", "By Category", "By Tag"];

/**
 * @param {object} params
 * @param {Array}   params.list      - rows from Redux state.purchaseRecord.list
 * @param {object}  params.filters   - filterType + recordType
 * @param {object}  params.authInfo  - for taxLabel + GST columns
 * @param {string}  params.title
 * @param {string}  params.filename
 */
export const exportPurchaseRecordCsv = ({
  list,
  filters,
  authInfo,
  title    = "Purchase Record",
  filename = "purchase_record",
}) => {
  const isIndia      = authInfo?.country?.toLowerCase() === "india";
  const isGstEnabled = authInfo?.gst_enabled === "yes";
  const showGst      = isIndia && isGstEnabled;
  const taxLabel     = isIndia ? "GST" : "VAT";
  const { filterType, recordType } = filters;
  const isItemBased  = itemFamily.includes(filterType);

  let headers = [];
  let buildRow;
  let buildTotalRow;

  // ── By Tag ────────────────────────────────────────────────────
  if (filterType === "By Tag") {
    headers = [
      "No.", "Voucher No", "Date", "Supplier", "Item Name", "Item Code",
      "Qty", "Rate", "Discount%", "VAT%", "Total", "Tag Group", "Tag Field",
    ];
    buildRow = (row, i) => [
      i + 1, row.vch_no || "", fmtDate(row.date),
      row.supplier || "", row.item_name || "", row.display_text || "",
      row.item_qty, money(row.item_rate),
      money(row.item_discount_per), money(row.item_tax_per), money(row.item_total),
      row.group_name || "", row.field_name || "",
    ];

  // ── By Item / By Group / By Category ─────────────────────────
  } else if (isItemBased) {
    headers = [
      "No.", "Voucher No", "Date", "Supplier", "Item Name", "Item Code",
      "Qty", "Rate", "Discount%", "VAT%", "Total",
    ];
    buildRow = (row, i) => [
      i + 1, row.vch_no || "", fmtDate(row.date),
      row.supplier || "", row.item_name || "", row.display_text || "",
      row.item_qty, money(row.item_rate),
      money(row.item_discount_per), money(row.item_tax_per), money(row.item_total),
    ];

  // ── With Item ─────────────────────────────────────────────────
  } else if (recordType === "with item") {
    headers = [
      "No.", "VCH No", "Date", "Supplier / Creditor", "Invoice No",
      "Created By", "Item", "Item Code",
      "Rate", "Qty", "Discount%", "VAT%", "Total",
    ];
    buildRow = (row, i) => [
      i + 1, row.vch_no || "", fmtDate(row.date),
      row.supplier || "", row.pur_invoice_no || "",
      row.createdBy || "",
      row.item_name || "", row.display_text || "",
      money(row.item_rate), row.item_qty,
      money(row.item_discount_per), money(row.item_tax_per), money(row.item_total),
    ];

  // ── Without Item (summary) ────────────────────────────────────
  } else {
    headers = [
      "No.", "Voucher No", "Date", "Supplier / Creditor", "Invoice No",
      "Created By", "Sub Total", "Discount", taxLabel,
      ...(showGst ? ["CGST", "SGST", "IGST"] : []),
      "Service", "Total", "Paid", "Due",
    ];
    buildRow = (row, i) => {
      const subTotal = Number(row.sub_total || 0) + Number(row.total_discount || 0) - Number(row.total_tax || 0);
      return [
        i + 1,
        row.vch_no          || "",
        fmtDate(row.date),
        row.supplier        || "",
        row.pur_invoice_no  || "",
        row.createdBy       || "",
        money(subTotal),
        money(row.total_discount),
        money(row.total_tax),
        ...(showGst ? [
          money(row.cgst_amount),
          money(row.sgst_amount),
          money(row.igst_amount),
        ] : []),
        money(row.other_service_cost),
        money(row.total_amount),
        money(row.paid_total),
        money(row.due_total),
      ];
    };
  }

  // ── Build totals row ──────────────────────────────────────────
  if (isItemBased || recordType === "with item") {
    const masterQty = list.reduce((s, r) => s + Number(r.item_qty   || 0), 0);
    const baseQty   = list.reduce((s, r) => s + Number(r.retail_qty || 0), 0);
    const total     = list.reduce((s, r) => s + Number(r.item_total || 0), 0);
    buildTotalRow = () => {
      const row = Array(headers.length).fill("");
      row[0]  = "Grand Total:";
      row[headers.length - 4] = `Master Qty: ${money(masterQty)}`;
      row[headers.length - 3] = `Base Qty: ${money(baseQty)}`;
      row[headers.length - 1] = money(total);
      return row;
    };
  } else {
    buildTotalRow = () => {
      const t = list.reduce((acc, row) => {
        const subTotal = Number(row.sub_total || 0) + Number(row.total_discount || 0) - Number(row.total_tax || 0);
        acc.subtotal  += subTotal;
        acc.discount  += Number(row.total_discount    || 0);
        acc.vat       += Number(row.total_tax         || 0);
        acc.cgst      += Number(row.cgst_amount       || 0);
        acc.sgst      += Number(row.sgst_amount       || 0);
        acc.igst      += Number(row.igst_amount       || 0);
        acc.service   += Number(row.other_service_cost|| 0);
        acc.total     += Number(row.total_amount      || 0);
        acc.paid      += Number(row.paid_total        || 0);
        acc.due       += Number(row.due_total         || 0);
        return acc;
      }, { subtotal: 0, discount: 0, vat: 0, cgst: 0, sgst: 0, igst: 0, service: 0, total: 0, paid: 0, due: 0 });

      return [
        "Grand Total:", "", "", "", "", "",
        money(t.subtotal), money(t.discount), money(t.vat),
        ...(showGst ? [money(t.cgst), money(t.sgst), money(t.igst)] : []),
        money(t.service), money(t.total), money(t.paid), money(t.due),
      ];
    };
  }

  // ── Assemble ──────────────────────────────────────────────────
  const rows = [
    [title],
    [],
    headers.map(escape),
    ...list.map((row, i) => buildRow(row, i).map(escape)),
    [],
    buildTotalRow().map(escape),
  ];

  const csv  = rows.map((r) => r.join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};